import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect} from 'react';
import equal from 'react-fast-compare';

import {MaybeArchivableItem} from 'archive/ArchiveConstants';
import getArchiveAwareDisplayName from 'archive/getArchiveAwareDisplayName';
import {assert} from 'Asserts';
import {getBenchlingAppConfigurationQuery} from 'benchling-apps/configuration/__generated/getBenchlingAppConfigurationQuery';
import {pluralize} from 'BenchlingUtil';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import deepCopy from 'lib/deepCopy';
import getIconAndLabel from 'registry/field/GetFieldLabelAndIcon';
import {
  FIELD_TYPES_BY_TYPE_STRING,
  getFolderItemType,
  REQUIRED_LINK_FOLDER_ITEM_TYPES_BY_TYPE_STRING,
} from 'schemas/fields/SchemaFieldConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import ChipPopoverContent, {ChipPopoverInfo} from 'ui-library/chips/ChipPopoverContent';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {capitalize, capwords, typeToName} from 'util/NameUtil';

import {
  ConfigValidationErrorTypes,
  DependencyConfigValidation,
  ValidationError,
  ValidationResult,
} from '../validation/ValidationTypes';

type Subdependency = NonNullable<
  ExtractByTypename<
    getBenchlingAppConfigurationQuery['benchlingApp'],
    'BenchlingAppNode'
  >['configuration'][number]['fieldDefinitions']
>[number];

type FieldConstraints = NonNullable<Subdependency['fieldConstraints']>;

export type SubdependencyArray = Array<Subdependency>;
export interface SubdependencyOption {
  id: string;
  name: string;
  // Keep in sync with FieldConstraints except without __typename
  fieldConstraints?: {
    internalType: string | null;
    isRequired: boolean | null;
    isMulti: boolean | null;
  };
  field?: SchemaField;
}

export interface BaseSubdependencyListProps {
  // oneOf options or fieldDefinitions as queried from the backend. If value is populated, the
  // subdependency is linked.
  subdependencies: SubdependencyArray;
  onChange: (newSubdependencies: SubdependencyArray) => void;
  disabled: boolean;
  isLoading: boolean;
  // The list of available options which a given subdependency may be associated with.
  subdependencyOptions: Array<SubdependencyOption>;
  // The human readable string describing the type of this subdependency list.
  subdependencyType: string;
  parentdependencyType?: string;
  fieldName?: string;
  updateDependencyConfigValidation: (dependencyValidation: DependencyConfigValidation) => void;
  dependencyValidation: DependencyConfigValidation;
  isWorkflowOutput?: boolean;
}

export function LoadingBaseSubdependencyList({
  subdependencies,
  subdependencyType,
  parentdependencyType,
}: {
  subdependencies: SubdependencyArray;
  subdependencyType: string;
  parentdependencyType: string;
}): JSX.Element {
  return (
    <BaseSubdependencyList
      subdependencies={subdependencies}
      onChange={noop}
      updateDependencyConfigValidation={noop}
      dependencyValidation={{errors: [], subdependencyValidation: {}, outputSubdependencyValidation: {}}}
      disabled={false}
      isLoading={true}
      subdependencyOptions={[]}
      subdependencyType={subdependencyType}
      parentdependencyType={parentdependencyType}
      isWorkflowOutput={false}
    />
  );
}

const getReadableType = (internalType: string): string => {
  if (REQUIRED_LINK_FOLDER_ITEM_TYPES_BY_TYPE_STRING.hasOwnProperty(internalType)) {
    return capitalize(typeToName(REQUIRED_LINK_FOLDER_ITEM_TYPES_BY_TYPE_STRING[internalType].type));
  } else if (FIELD_TYPES_BY_TYPE_STRING.hasOwnProperty(internalType)) {
    return FIELD_TYPES_BY_TYPE_STRING[internalType].label;
  } else {
    assert(
      REQUIRED_LINK_FOLDER_ITEM_TYPES_BY_TYPE_STRING.hasOwnProperty(internalType) ||
        FIELD_TYPES_BY_TYPE_STRING.hasOwnProperty(internalType),
      'internal type provided is not valid.',
      {internalType, REQUIRED_LINK_FOLDER_ITEM_TYPES_BY_TYPE_STRING, FIELD_TYPES_BY_TYPE_STRING}
    );
    return 'Never Reached'; // Used to satisfy type checker.
  }
};

const generateConstraintError = (
  constraint: FieldConstraints,
  option: SubdependencyOption
): string | null => {
  const errors: Array<string> = [];
  if (option.fieldConstraints == null) {
    return "This doesn't match configuration type.";
  }

  if (constraint.internalType != null) {
    let optionInternalType = option.fieldConstraints.internalType;
    if (optionInternalType === 'ft_file_link' && option.field != null) {
      optionInternalType = getFolderItemType(option.field);
    }
    if (optionInternalType !== constraint.internalType) {
      const readableConstraintType = getReadableType(constraint.internalType);
      errors.push(`This doesn't match configuration type. ${readableConstraintType} field required.`);
    }
  }

  if (constraint.isRequired != null && option.fieldConstraints.isRequired !== constraint.isRequired) {
    errors.push(
      `This doesn't match required setting. Expected this field to be ${
        constraint.isRequired ? 'Required' : 'Optional'
      }.`
    );
  }

  if (constraint.isMulti != null && option.fieldConstraints.isMulti !== constraint.isMulti) {
    errors.push(
      `This doesn't match multi-select setting. Expected this field to be ${
        constraint.isMulti ? 'Multi-select' : 'Single-select'
      }.`
    );
  }

  if (errors.length === 0) {
    return null;
  }
  return errors.length === 1 ? errors[0] : `• ${errors.join('\n• ')}`;
};

const getSubdependencyErrorMessage = (validationErrors: Array<ValidationError>): string => {
  if (validationErrors.length === 0) {
    return '';
  }

  return validationErrors.some((err) => err.type === ConfigValidationErrorTypes.RequiredConfigError)
    ? 'Field is required'
    : 'Linked field is invalid';
};

const validateLinkedResource = ({
  selectedValue,
  maybeConstraints,
  options,
  requiredConfig,
  name,
}: {
  selectedValue: string | null;
  maybeConstraints: FieldConstraints | null;
  options: Array<SubdependencyOption>;
  requiredConfig: boolean;
  name: string;
}): ValidationResult => {
  const errors: Array<ValidationError> = [];

  if (requiredConfig && !selectedValue) {
    errors.push({type: ConfigValidationErrorTypes.RequiredConfigError, message: `${name} is required`});
  }

  if (!(maybeConstraints && selectedValue)) {
    return {errors};
  }

  const constraints = maybeConstraints;
  const maybeSelectedOption = options.find((option) => option.id === selectedValue);

  if (!maybeSelectedOption) {
    return {errors};
  }

  const selectedOption = maybeSelectedOption;
  const constraintError = generateConstraintError(constraints, selectedOption);

  if (constraintError != null) {
    errors.push({type: ConfigValidationErrorTypes.FieldConstraintsError, message: constraintError});
  }

  return {errors};
};

function generatePopoverFields(subdependency: Subdependency): ChipPopoverInfo {
  return {
    fields: [
      {
        label: 'Description',
        value: subdependency.description ?? <div className={css(styles.unset)}>None</div>,
      },
      {
        label: 'Required',
        value:
          subdependency.fieldConstraints &&
          (subdependency.fieldConstraints.isRequired == null
            ? 'Any'
            : subdependency.fieldConstraints.isRequired
              ? 'True'
              : 'False'),
      },
      {
        label: 'Multi-select',
        value:
          subdependency.fieldConstraints &&
          (subdependency.fieldConstraints.isMulti == null
            ? 'Any'
            : subdependency.fieldConstraints.isMulti
              ? 'True'
              : 'False'),
      },
      {
        label: 'Definition',
        value:
          subdependency.fieldConstraints &&
          (subdependency.fieldConstraints.internalType != null
            ? getReadableType(subdependency.fieldConstraints.internalType)
            : 'Any'),
      },
    ],
  };
}

function SubdependencyName({subdependency}: {subdependency: Subdependency}): JSX.Element {
  return (
    <div>
      <Popover
        renderContent={() => <ChipPopoverContent info={generatePopoverFields(subdependency)} />}
        placement={FloatingPlacement.RIGHT}
      >
        <FieldLabel
          label={subdependency.name}
          labelStyle={LabelStyle.SUBLABEL}
          isRequired={subdependency.requiredConfig}
        >
          {subdependency.name}
        </FieldLabel>
      </Popover>
    </div>
  );
}

export default function BaseSubdependencyList({
  subdependencies,
  onChange,
  dependencyValidation,
  updateDependencyConfigValidation,
  disabled,
  isLoading,
  subdependencyOptions,
  subdependencyType,
  parentdependencyType,
  isWorkflowOutput = false,
}: BaseSubdependencyListProps): JSX.Element {
  const capitalizedPluralizedSubdependencyType = capwords(pluralize(2, subdependencyType));
  const baseMenuOptions: Array<OptionData> = [
    {name: `No ${subdependencyType}`, value: null},
    {name: capitalizedPluralizedSubdependencyType, isHeader: true},
  ];
  const subdepValidation = isWorkflowOutput
    ? dependencyValidation.outputSubdependencyValidation
    : dependencyValidation.subdependencyValidation;

  useEffect(() => {
    const newDependencyValidation = deepCopy(dependencyValidation);
    const subdepValidationToUpdate = isWorkflowOutput
      ? newDependencyValidation.outputSubdependencyValidation
      : newDependencyValidation.subdependencyValidation;
    const newSubdepValidation = Object.assign(
      subdepValidationToUpdate,
      ...subdependencies.map((subdep) => ({
        [subdep.name]: validateLinkedResource({
          selectedValue: subdep.value,
          maybeConstraints: subdep.fieldConstraints,
          options: subdependencyOptions,
          requiredConfig: subdep.requiredConfig,
          name: subdep.name,
        }),
      }))
    );

    if (isWorkflowOutput) {
      newDependencyValidation.outputSubdependencyValidation = newSubdepValidation;
    } else {
      newDependencyValidation.subdependencyValidation = newSubdepValidation;
    }

    // Prevent an infinite loop since this gets retriggered by a new dependency validation prop
    if (!equal(newDependencyValidation, dependencyValidation)) {
      updateDependencyConfigValidation(newDependencyValidation);
    }
  }, [
    isWorkflowOutput,
    subdependencies,
    dependencyValidation,
    subdependencyOptions,
    updateDependencyConfigValidation,
  ]);

  function getOptionDisplayName(id: string | null, options: Array<MaybeArchivableItem>): string {
    if (id == null) {
      return `No ${subdependencyType} selected`;
    }
    const selectedOption = options.find((option) => option.id === id);
    if (selectedOption) {
      return getArchiveAwareDisplayName(selectedOption);
    } else {
      return `Inaccessible ${subdependencyType}`;
    }
  }

  function handleOnSelect(newSelectedValue: string, i: number): void {
    const newSubdependencies = [
      ...subdependencies.slice(0, i),
      {...subdependencies[i], value: newSelectedValue},
      ...subdependencies.slice(i + 1),
    ];
    onChange(newSubdependencies);
  }

  // This function is purely a helper for getting the dropdown icon
  function maybeGetDefaultSchemaFieldSelectors(field: SchemaField): Array<SchemaFieldSelector> {
    if (field.schemaFieldSelectorId) {
      return [
        {
          name: field.name,
          id: field.schemaFieldSelectorId,

          // These attributes aren't used
          schemaFieldSelectorOptions: [],
          allSchemaFieldSelectorOptions: [],
        },
      ];
    }

    return [];
  }

  return (
    <div className={css(styles.container)}>
      <div className="u-lr-split">
        <FieldLabel
          label={`App ${capitalizedPluralizedSubdependencyType}`}
          labelStyle={FieldLabel.STYLES.LABEL}
          className={css(styles.subdepsLabelLeft)}
        />
        <FieldLabel
          label={
            parentdependencyType
              ? `${parentdependencyType} ${capitalizedPluralizedSubdependencyType}`
              : `${capitalizedPluralizedSubdependencyType}`
          }
          labelStyle={FieldLabel.STYLES.LABEL}
          className={css(styles.subdepsLabelRight)}
        />
      </div>
      {subdependencies.map((subdependency, i) => (
        <div key={i} className={css(styles.row)}>
          <div className="u-lr-split">
            <SubdependencyName subdependency={subdependency} />
            <div>
              {isLoading ? (
                <CenteredHorizontalThrobber />
              ) : (
                <SelectDropdown
                  displayName={getOptionDisplayName(subdependency.value, subdependencyOptions)}
                  disabled={disabled}
                  menu={
                    <SearchableMenu
                      onSelect={(newSelectedValue) => {
                        handleOnSelect(newSelectedValue, i);
                      }}
                      optionDatas={baseMenuOptions.concat(
                        subdependencyOptions
                          .map((option) => {
                            const maybeMessage = subdependency.fieldConstraints
                              ? generateConstraintError(subdependency.fieldConstraints, option)
                              : null;
                            const icon = option.field
                              ? getIconAndLabel({
                                  field: option.field,
                                  schemaFieldSelectors: maybeGetDefaultSchemaFieldSelectors(option.field),
                                  ownerUrlId: '', // Because we don't include the registry this is never used
                                }).icon
                              : undefined;
                            return {
                              name: option.name,
                              value: option.id,
                              disabledMessage: maybeMessage ?? '',
                              disabled: maybeMessage != null,
                              icon,
                            };
                          })
                          .sort((a, b) => (a.disabled === b.disabled ? 0 : a.disabled ? 1 : -1))
                      )}
                      selectedValue={subdependency.value}
                    />
                  }
                  buttonClassName={css(styles.subdepsDropdown)}
                  error={getSubdependencyErrorMessage(subdepValidation[subdependency.name]?.errors || [])}
                />
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  row: {
    marginTop: '10px',
    padding: '10px 70px 0px 10px',
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  container: {
    marginTop: '20px',
  },
  subdepsLabelLeft: {
    margin: '0px 0px 0px 10px',
  },
  subdepsLabelRight: {
    marginBottom: '0px',
    width: '429px',
  },
  subdepsDropdown: {
    width: '360px',
  },
  unset: {
    color: Colors.GRAY_DARK,
  },
});
